/**
 * GiraffeMemoirPrint — print-optimized, cinematic PDF export for a trip memoir.
 *
 * Paired with GiraffeMemoir. Opens in a new tab from the memoir's overflow menu,
 * auto-triggers window.print() once all images have loaded so Werner's Chrome
 * dialog saves it as a PDF.
 *
 * Layout: A4 PORTRAIT (Chrome's default — explicit landscape in CSS is ignored
 * unless the user flips the dialog toggle). One fragment per full-bleed page,
 * obsidian background, Giraffe-green accents.
 *
 * Chrome's "Background graphics" checkbox MUST be on for the obsidian background
 * to render — the instruction bar reminds the user.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';

const GIRAFFE_GREEN = '#10B981';
const OBSIDIAN = '#0a0a0f';

const PRINT_STYLES = `
  @page {
    size: A4;
    margin: 0;
  }

  html, body {
    background: ${OBSIDIAN} !important;
    color: #f0f0f5;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    margin: 0;
    padding: 0;
  }

  * {
    box-sizing: border-box;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .mp-page {
    width: 210mm;
    height: 297mm;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;
    background: ${OBSIDIAN};
    color: #f0f0f5;
    margin: 0;
    padding: 0;
    break-after: page;
    break-inside: avoid;
  }

  .mp-page:last-child {
    break-after: auto;
  }

  /* Fragment photos: anchored to top, letterbox space falls below the photo
     and sits naturally above the text block / gradient. */
  .mp-bleed {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center top;
    background: ${OBSIDIAN};
  }

  /* Cover hero: centered, shown in full. */
  .mp-bleed-cover {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center center;
    background: ${OBSIDIAN};
  }

  .mp-grad-bottom {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 55%;
    background: linear-gradient(to top, rgba(10,10,15,0.96) 0%, rgba(10,10,15,0.80) 35%, rgba(10,10,15,0.35) 75%, rgba(10,10,15,0) 100%);
    pointer-events: none;
  }

  .mp-grad-cover {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(10,10,15,0.88) 0%, rgba(10,10,15,0.45) 45%, rgba(10,10,15,0.15) 100%);
    pointer-events: none;
  }

  /* ─── Cover ─────────────────────────────────────────── */
  .mp-cover-text {
    position: absolute;
    left: 18mm;
    right: 18mm;
    bottom: 22mm;
    color: #f0f0f5;
  }

  .mp-cover-rule {
    width: 52mm;
    height: 1px;
    background: ${GIRAFFE_GREEN};
    margin-bottom: 9mm;
  }

  .mp-cover-title {
    font-family: 'Playfair Display', Georgia, serif;
    font-weight: 500;
    font-size: 46pt;
    line-height: 1.02;
    margin: 0 0 7mm 0;
    letter-spacing: -0.01em;
    color: #ffffff;
  }

  .mp-cover-meta {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10pt;
    color: rgba(240,240,245,0.72);
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin: 0;
  }

  .mp-cover-meta + .mp-cover-meta {
    margin-top: 3mm;
  }

  /* ─── Photo fragment (full bleed, text at bottom) ─────── */
  .mp-photo-text {
    position: absolute;
    left: 18mm;
    right: 18mm;
    bottom: 20mm;
    max-width: 155mm;
  }

  .mp-day-label {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 8.5pt;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: ${GIRAFFE_GREEN};
    margin: 0 0 5mm 0;
  }

  .mp-body {
    font-family: 'Playfair Display', Georgia, serif;
    font-weight: 400;
    font-size: 17pt;
    line-height: 1.42;
    color: #f5f5fa;
    margin: 0 0 6mm 0;
    white-space: pre-wrap;
  }

  .mp-location {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 8pt;
    color: rgba(240,240,245,0.58);
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin: 0;
  }

  /* ─── Quote page ───────────────────────────────────── */
  .mp-quote-wrap {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 26mm;
  }

  .mp-quote-mark {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 110pt;
    line-height: 0.7;
    color: rgba(16,185,129,0.22);
    margin-bottom: 4mm;
    align-self: flex-start;
    margin-left: -2mm;
  }

  .mp-quote-text {
    font-family: 'Playfair Display', Georgia, serif;
    font-style: italic;
    font-weight: 400;
    font-size: 22pt;
    line-height: 1.45;
    color: #f0f0f5;
    text-align: left;
    margin: 0 0 12mm 0;
  }

  .mp-quote-attr {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 9pt;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: rgba(240,240,245,0.62);
    margin: 0;
    align-self: flex-start;
  }

  .mp-quote-footer {
    position: absolute;
    bottom: 18mm;
    left: 0;
    right: 0;
    text-align: center;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 7.5pt;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(240,240,245,0.38);
  }

  /* ─── Page number ─────────────────────────────────── */
  .mp-pageno {
    position: absolute;
    bottom: 8mm;
    left: 0;
    right: 0;
    text-align: center;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 7pt;
    letter-spacing: 0.34em;
    color: rgba(240,240,245,0.32);
  }

  /* ─── Colophon ────────────────────────────────────── */
  .mp-colophon {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 24mm;
    color: #f0f0f5;
    text-align: center;
  }

  .mp-colophon-title {
    font-family: 'Playfair Display', Georgia, serif;
    font-style: italic;
    font-weight: 400;
    font-size: 22pt;
    color: #f0f0f5;
    margin: 0 0 11mm 0;
  }

  .mp-colophon-rule {
    width: 44mm;
    height: 1px;
    background: ${GIRAFFE_GREEN};
    margin: 0 0 10mm 0;
  }

  .mp-colophon-table {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10pt;
    color: rgba(240,240,245,0.72);
    line-height: 2;
    letter-spacing: 0.08em;
  }

  .mp-colophon-table dt {
    display: inline-block;
    width: 32mm;
    text-align: right;
    padding-right: 6mm;
    color: rgba(240,240,245,0.42);
    text-transform: uppercase;
    font-size: 8pt;
    letter-spacing: 0.2em;
    vertical-align: baseline;
  }

  .mp-colophon-table dd {
    display: inline-block;
    margin: 0;
    text-align: left;
    color: #f0f0f5;
  }

  .mp-colophon-row {
    margin: 0;
  }

  .mp-colophon-wordmark {
    position: absolute;
    bottom: 14mm;
    left: 0;
    right: 0;
    text-align: center;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 7pt;
    letter-spacing: 0.42em;
    color: rgba(240,240,245,0.26);
  }

  /* ─── Screen preview ──────────────────────────────── */
  @media screen {
    body {
      background: #050507 !important;
      padding: 40px 0;
    }
    .mp-page {
      margin: 0 auto 24px auto;
      box-shadow: 0 16px 48px rgba(0,0,0,0.6);
    }
  }

  @media print {
    body { padding: 0 !important; margin: 0 !important; background: ${OBSIDIAN} !important; }
    .mp-page { margin: 0 !important; padding: 0 !important; box-shadow: none !important; }
  }
`;

function hasImage(entry) {
  return !!(entry.composed_image_url || entry.composed_url || (entry.images && entry.images.length > 0) || entry.media_type === 'photo');
}

function isComposed(entry) {
  return !!(entry.composed_image_url || entry.composed_url);
}

function isQuote(entry) {
  return entry.media_type === 'quote' || (!hasImage(entry) && !!entry.quote_text);
}

function getFragmentImage(entry, backend) {
  if (entry.composed_image_url) return entry.composed_image_url;
  if (entry.composed_url) return entry.composed_url;
  if (entry.images && entry.images.length > 0) {
    const url = entry.images[0];
    return url?.startsWith('/') ? `${backend}${url}` : url;
  }
  return null;
}

function formatDateLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase();
}

function dayCount(start, end) {
  if (!start) return null;
  const s = new Date(start);
  const e = end ? new Date(end) : s;
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
  const ms = e.getTime() - s.getTime();
  return Math.max(1, Math.round(ms / 86400000) + 1);
}

// Wait for all <img> elements to finish loading (or error out) before proceeding.
function waitForAllImages(timeoutMs = 15000) {
  const imgs = Array.from(document.images);
  if (imgs.length === 0) return Promise.resolve();
  return Promise.race([
    Promise.all(imgs.map(img =>
      img.complete && img.naturalHeight > 0
        ? Promise.resolve()
        : new Promise(res => {
            const done = () => { img.removeEventListener('load', done); img.removeEventListener('error', done); res(); };
            img.addEventListener('load', done);
            img.addEventListener('error', done);
          })
    )),
    new Promise(res => setTimeout(res, timeoutMs)),
  ]);
}

export default function GiraffeMemoirPrint() {
  const { slug } = useParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/giraffe/memoir/${slug}`)
      .then(r => {
        setTrip(r.data);
        document.title = `${r.data.title} — Memoir`;
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Auto-print once trip + all images are loaded. Previously this fired on a
  // blind 1.5s timeout, which printed before the Cloudinary hero resolved.
  useEffect(() => {
    if (!trip) return;
    if (!window.location.search.includes('auto=1')) return;
    let cancelled = false;
    // One tick so React paints, then wait for images.
    const tick = setTimeout(async () => {
      await waitForAllImages();
      if (cancelled) return;
      // Small buffer so decoders flush.
      setTimeout(() => { if (!cancelled) window.print(); }, 300);
    }, 200);
    return () => { cancelled = true; clearTimeout(tick); };
  }, [trip]);

  const backend = process.env.REACT_APP_BACKEND_URL || '';

  const fragments = useMemo(() => {
    if (!trip) return [];
    return (trip.entries || [])
      .filter(e => (e.entry_type === 'fragment' || e.entry_type === 'memoir' || !e.entry_type))
      .filter(e => hasImage(e) || isQuote(e));
  }, [trip]);

  const coverImage = useMemo(() => {
    if (!trip) return null;
    if (trip.cover_image) {
      const url = trip.cover_image;
      return url.startsWith('/') ? `${backend}${url}` : url;
    }
    for (const e of fragments) {
      const img = getFragmentImage(e, backend);
      if (img) return img;
    }
    return null;
  }, [trip, fragments, backend]);

  const locations = useMemo(() => {
    const set = new Set();
    fragments.forEach(f => { if (f.location) set.add(f.location); });
    return Array.from(set);
  }, [fragments]);

  const photoCount = fragments.filter(f => hasImage(f)).length;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: OBSIDIAN, color: '#f0f0f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, letterSpacing: '0.15em' }}>
        LOADING MEMOIR...
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div style={{ minHeight: '100vh', background: OBSIDIAN, color: '#f0f0f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Playfair Display', serif", fontSize: 18 }}>
        Memoir not found.
      </div>
    );
  }

  const totalFragmentPages = fragments.length;
  const days = dayCount(trip.start_date, trip.end_date);
  const dateRange = trip.start_date
    ? `${formatDateLabel(trip.start_date)}${trip.end_date ? ` → ${formatDateLabel(trip.end_date)}` : ''}`
    : '';

  return (
    <>
      <style>{PRINT_STYLES}</style>

      {/* Cover */}
      <div className="mp-page" data-testid="memoir-print-cover">
        {coverImage ? (
          <>
            <img className="mp-bleed-cover" src={coverImage} alt="" />
            <div className="mp-grad-cover" />
          </>
        ) : null}
        <div className="mp-cover-text">
          <div className="mp-cover-rule" />
          <h1 className="mp-cover-title">{trip.title}</h1>
          {dateRange && <p className="mp-cover-meta">{dateRange}</p>}
          {locations.length > 0 && (
            <p className="mp-cover-meta">{locations.slice(0, 5).join('  ·  ')}</p>
          )}
        </div>
      </div>

      {/* Fragment pages */}
      {fragments.map((entry, i) => {
        const pageNum = i + 1;
        const dayLabel = entry.title
          ? entry.title.toUpperCase()
          : (entry.date ? formatDateLabel(entry.date) : `DAY ${pageNum}`);

        if (isComposed(entry)) {
          const img = getFragmentImage(entry, backend);
          return (
            <div className="mp-page" key={entry.id || i} data-testid="memoir-print-page">
              {img && <img className="mp-bleed" src={img} alt="" />}
              <div className="mp-pageno">{pageNum} / {totalFragmentPages}</div>
            </div>
          );
        }

        if (hasImage(entry)) {
          const img = getFragmentImage(entry, backend);
          return (
            <div className="mp-page" key={entry.id || i} data-testid="memoir-print-page">
              {img && <img className="mp-bleed" src={img} alt="" />}
              <div className="mp-grad-bottom" />
              <div className="mp-photo-text">
                <p className="mp-day-label">{dayLabel}</p>
                {entry.content && <p className="mp-body">{entry.content}</p>}
                {entry.location && <p className="mp-location">{entry.location}</p>}
              </div>
              <div className="mp-pageno">{pageNum} / {totalFragmentPages}</div>
            </div>
          );
        }

        // Quote
        return (
          <div className="mp-page" key={entry.id || i} data-testid="memoir-print-page">
            <div className="mp-quote-wrap">
              <span className="mp-quote-mark">&ldquo;</span>
              <p className="mp-quote-text">{entry.quote_text || entry.content}</p>
              {entry.quote_author && (
                <p className="mp-quote-attr">— {entry.quote_author}</p>
              )}
            </div>
            <div className="mp-quote-footer">
              {entry.date ? formatDateLabel(entry.date) : dayLabel}
            </div>
            <div className="mp-pageno">{pageNum} / {totalFragmentPages}</div>
          </div>
        );
      })}

      {/* Colophon */}
      <div className="mp-page" data-testid="memoir-print-colophon">
        <div className="mp-colophon">
          <p className="mp-colophon-title">{trip.title}</p>
          <div className="mp-colophon-rule" />
          <dl className="mp-colophon-table">
            {dateRange && (
              <p className="mp-colophon-row"><dt>Dates</dt><dd>{dateRange}</dd></p>
            )}
            {days !== null && (
              <p className="mp-colophon-row"><dt>Days</dt><dd>{days}</dd></p>
            )}
            {locations.length > 0 && (
              <p className="mp-colophon-row"><dt>Places</dt><dd>{locations.slice(0, 8).join(', ')}</dd></p>
            )}
            <p className="mp-colophon-row"><dt>Fragments</dt><dd>{fragments.length}</dd></p>
            <p className="mp-colophon-row"><dt>Photos</dt><dd>{photoCount}</dd></p>
          </dl>
        </div>
        <div className="mp-colophon-wordmark">WERNER LABS · GIRAFFE</div>
      </div>
    </>
  );
}
